import { useSyncExternalStore, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Globe, Mail, MapPin, Phone, Smartphone, Users } from 'lucide-react';
import type { PersonInfo } from '@kaba/shared';
import { api } from '../lib/api';
import { Avatar, Sheet, cx } from './ui/ui';

/* Eine Person ansehen (Profil, Kontakt) – von überall per openPerson(id) */
let current: string | null = null;
const listeners = new Set<() => void>();
const set = (id: string | null) => {
  current = id;
  listeners.forEach((l) => l());
};
export const openPerson = (id: string) => set(id);
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** Name als Schaltfläche, die das Profil öffnet */
export function PersonName({ id, name, className, children }: { id: string; name: string; className?: string; children?: ReactNode }) {
  return (
    <button
      type="button"
      className={cx('max-w-full truncate rounded text-left underline-offset-2 hover:underline focus-visible:underline max-sm:inline-flex max-sm:min-h-11 max-sm:items-center', className)}
      onClick={(e) => {
        e.stopPropagation();
        openPerson(id);
      }}
      aria-label={`Profil von ${name}`}
    >
      {children ?? name}
    </button>
  );
}

/** „Funktion · Firma“ unter dem Namen */
export function Headline({ text }: { text?: string }) {
  return text ? <p className="truncate text-xs text-[var(--ink-soft)]">{text}</p> : null;
}

const telHref = (v: string) => `tel:${v.replace(/[^0-9+]/g, '')}`;

function Row({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="min-h-11 py-1.5">
      <dt className="flex items-center gap-2 text-xs text-[var(--ink-soft)]">
        <span aria-hidden>{icon}</span>
        {label}
      </dt>
      <dd className="pl-[22px] text-sm break-words">{children}</dd>
    </div>
  );
}

const linkCls = 'text-accent-fg underline underline-offset-2';

function PersonBody({ id }: { id: string }) {
  const { data: p, isLoading, error } = useQuery({ queryKey: ['person', id], queryFn: () => api.get<PersonInfo>(`/api/people/${id}`), staleTime: 60_000 });
  if (isLoading) return <p className="p-4 text-sm text-[var(--ink-soft)]">Lädt …</p>;
  if (error || !p) return <p className="p-4 text-sm">{(error as Error)?.message ?? 'Profil nicht verfügbar.'}</p>;
  const pr = p.profile;
  const headline = pr ? [pr.jobTitle, pr.company].filter(Boolean).join(' · ') : '';
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-4">
        <Avatar user={p} size={88} />
        <div className="min-w-0">
          <p className="text-lg font-semibold break-words">{p.name}</p>
          {headline && <p className="text-sm text-[var(--ink-soft)]">{headline}</p>}
          {p.guest && <p className="mt-1 text-xs text-[var(--ink-soft)]">Gast über einen Gastlink</p>}
        </div>
      </div>
      <dl className="flex flex-col divide-y divide-[var(--line)]">
        {p.email && (
          <Row icon={<Mail size={14} />} label="E-Mail">
            <a className={linkCls} href={`mailto:${p.email}`}>
              {p.email}
            </a>
          </Row>
        )}
        {pr?.phone && (
          <Row icon={<Phone size={14} />} label="Telefon">
            <a className={linkCls} href={telHref(pr.phone)}>
              {pr.phone}
            </a>
          </Row>
        )}
        {pr?.mobile && (
          <Row icon={<Smartphone size={14} />} label="Mobil">
            <a className={linkCls} href={telHref(pr.mobile)}>
              {pr.mobile}
            </a>
          </Row>
        )}
        {pr?.department && (
          <Row icon={<Users size={14} />} label="Abteilung / Bereich">
            {pr.department}
          </Row>
        )}
        {pr?.location && (
          <Row icon={<MapPin size={14} />} label="Standort">
            {pr.location}
          </Row>
        )}
        {pr?.website && /^https?:\/\//i.test(pr.website) && (
          <Row icon={<Globe size={14} />} label="Website">
            <a className={linkCls} href={pr.website} target="_blank" rel="noopener noreferrer nofollow">
              {pr.website.replace(/^https?:\/\//i, '')}
            </a>
          </Row>
        )}
      </dl>
      {pr?.about && (
        <div>
          <h3 className="text-xs text-[var(--ink-soft)]">Über mich</h3>
          <p className="mt-1 text-sm whitespace-pre-line">{pr.about}</p>
        </div>
      )}
      {!p.guest && !pr && <p className="text-sm text-[var(--ink-soft)]">{p.name} hat keine weiteren Angaben freigegeben.</p>}
    </div>
  );
}

/** Einmal in App eingehängt */
export function PersonDialogHost() {
  const id = useSyncExternalStore(subscribe, () => current);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && set(null)} title="Profil" side="center">
      {id ? <PersonBody id={id} /> : <span />}
    </Sheet>
  );
}
